import { compact } from "lodash";

export const SANDBOX_TITLE = (modeLabel: string) => `Testing Sandbox · ${modeLabel}`;

export const SANDBOX_DRAWER_WIDTH = 720;

export const INPUTS_TITLE = "Prompt variables";

export const INPUTS_DESCRIPTION =
  "The values this flow's prompts take, prefilled with each one's example. Whatever the flow does not ask for — vocabulary, lesson data — the backend fills in itself.";

export const INPUTS_NO_VARIABLES = "This flow takes no variables. The backend supplies everything it needs.";

export const VARIANT_LABEL = "Prompt under test";

export const ONBOARDING_VARIANT_LABEL: Record<string, string> = {
  onboarding_new_user: "New user",
  onboarding_reset: "Profile reset",
};

export const REQUIRED_MARK = "*";

export const FLUENCY_HINT = "below 3 also folds the beginner rules into the prompt";

/**
 * ROOM LEFT, IN THE UNIT THE PANEL ALREADY USES. A SCENARIO's only, which is where Wrap Up Round sits:
 * that control is set in rounds and the header counts them, so a warning in transcript turns made one
 * screen speak two units — and an operator reading "36 turns" against a limit of 18 rounds has nothing
 * on screen saying those are different scales. Naming the ceiling rather than the remainder also left
 * them to do the subtraction.
 *
 * NO DENOMINATOR, and that absence is deliberate. `round` is what the backend reported running; the
 * wrap-up limit is only readable from the input beside it, which holds what the NEXT run would use.
 * Printing the two together would read as one fact — and they come apart in the window after Start
 * where the opener has spoken but no learner turn has, so the input is still live.
 */
export const LIMIT_NEAR_ROUNDS = (round: number, roundsLeft: number) =>
  `Round ${round} · room for about ${roundsLeft} more ${roundsLeft === 1 ? "round" : "rounds"}.`;

/**
 * EVERY MODE WITH NO WRAP UP ROUND, which is onboarding and lesson review both — `hasWrapUp` is a
 * scenario alone. They arrive here for different reasons and turns is the honest unit for each:
 * lesson review reports `roundCount: 0` on every turn, its position being the cursor, while onboarding
 * does count rounds but ends on its own message limit rather than a round an operator set. Neither has
 * a figure on screen for a round to be read against.
 */
export const LIMIT_NEAR_TURNS = (turns: number, turnsLeft: number) =>
  `${turns} turns so far · room for about ${turnsLeft} more.`;

export const LIMIT_BLOCKED = "This conversation is full. Start a new one to keep testing.";

export const START_CONVERSATION = "Start Conversation";

export const SANDBOX_EMPTY =
  "Fill in the variables you want to test with, then start the conversation. Nothing here is saved and no learner quota is spent.";

export const SANDBOX_EMPTY_LESSON =
  "Pick the lesson script you want to walk, then start the conversation. Nothing here is saved and no learner quota is spent.";

export const THINKING_LABEL = "Waiting for the model";

export const MESSAGE_PLACEHOLDER = "Type what a learner would say";

export const SEND_MESSAGE = "Send";

export const HINT = "Hint";

export const HINT_LABEL = "Hint:";

export const HINT_HINT = "What the app would suggest the learner say next";

// 🔴 A null result is a real answer (§7): the hint service returns none when its prompt is misconfigured.
export const ACTION_NO_ANSWER = "The flow answered with nothing here — its prompt may be missing configuration.";

export const TRANSLATION_LABEL = "means:";

export const CHECK_GRAMMAR = "Check grammar";

export const GRAMMAR_CLEAN = "No errors found.";

export const GRAMMAR_CORRECTED = "Corrected:";

export const GRAMMAR_SCOPE = "Grammar only";

// 🔴 On a phrase match the backend never calls the correction model, so a missing result is not "no errors".
export const GRAMMAR_SKIPPED = "No grammar check this round — the answer matched the expected one closely enough.";

export const SUPPORT_FEEDBACK = "Feedback Summary";

export const SUPPORT_FEEDBACK_DESCRIPTION =
  "What the app tells the learner once a conversation is over. It reads the conversation on screen.";

export const SUPPORT_NEEDS_TURNS = "Say something in the conversation first — there is nothing to read yet.";

export const SUPPORT_SUMMARISE = "Summarise";

export const SUPPORT_ACHIEVEMENTS = "Achievements";

export const SUPPORT_SUGGESTIONS = "Suggestions";

export const SUPPORT_FEEDBACK_EMPTY = "The flow returned no summary for this conversation.";

export const SUPPORT_WORDS = "Foreign Words";

export const SUPPORT_WORDS_DESCRIPTION =
  "Which words in this conversation were not the target language, through the flow's own identification prompt. It reads the conversation on screen.";

export const SUPPORT_IDENTIFY = "Identify";

export const SUPPORT_WORDS_EMPTY = "The flow found no foreign words in this conversation.";

export const SUPPORT_WORDS_WORD = "Word";

export const SUPPORT_WORDS_MEANS = "Means";

export const WRAP_UP = "Wrap Up";

export const wrapUpHint = (isArmed: boolean, isSent: boolean, message: string) => {
  if (isArmed) {
    return "This conversation is already winding down: the instruction goes in by itself from here.";
  }

  if (isSent) {
    return "This conversation has already had its wind-down instruction.";
  }

  if (!message.trim()) {
    return "Type the learner's next line first — the instruction is appended to it, and a turn with no message only renders the prompt.";
  }

  return "Appends production's wind-down instruction to this message, without moving the round it would go in by itself.";
};

export const ROUNDS_LABEL = "Wrap up at round";

export const ROUNDS_HINT = (grace: number) =>
  `The round the wind-down instruction goes in by itself, as production does at 18. The same number sets where the conversation is cut off — ${grace} rounds later — so it is fixed once you have sent a message; Restart to change it.`;

export const FIRST_CONVERSATION_LABEL = "First conversation";

export const FIRST_CONVERSATION_HINT =
  "On: the opener runs conversation_starter_first, which walks a new learner through the buttons — production uses it until the learner completes a scenario. Off: conversation_starter, the opener every later conversation gets.";

export const OPENER_CHANGED = "The first-conversation setting changed since this conversation started.";

export const RESTART_WITH_OPENER = "Restart with this setting";

export const RESTART = "Restart";

export const DETAILS_UNSET = "nothing set";

export const DETAILS_CHANGED = "The variable values changed since this conversation started.";

export const RESTART_WITH_DETAILS = "Restart with these values";

export const VARIANT_CHANGED = "The onboarding prompt changed since this conversation started.";

export const RESTART_WITH_VARIANT = "Restart with this prompt";

export const PROMPT_SAVED = (name: string) => `${name} was saved. The sandbox now reads the saved version.`;

export const SAVED_SINCE_RUN = (names: string[]) => `Saved since this conversation started: ${names.join(", ")}.`;

export const RESTART_WITH_SAVED = "Restart with the saved prompts";

export const ENDED_BY_MODEL = "The model ended the conversation.";

export const ENDED_BY_GRACE = "Production would have stopped here: the wrap-up grace ran out.";

export const ENDED_GENERIC = "The conversation is over.";

export const ENDED_BY_SCRIPT = "The lesson script is finished — every learner line was answered.";

export const LESSON_TITLE = "Lesson script";

export const LESSON_DESCRIPTION =
  "The lines below are assembled from the lesson script, not written by a model. What the prompts decide is the verdict on each answer — shown under every reply, with the hint and the lesson correction that the same turn produced.";

export const LESSON_SCRIPT_OPTION = (index: number, title: string) => (title ? `${index} · ${title}` : String(index));

export const LESSON_SUMMARY = (level: number, lesson: number, index: number, title?: string) =>
  compact([`Level ${level}`, `Lesson ${lesson}`, title || `Lesson Index ${index}`]).join(" · ");

export const LESSON_PREVIEW_LEARNER = "learner";

export const LESSON_PREVIEW_CUE = "cue:";

export const LESSON_NO_SCRIPTS =
  "No lesson script for this choice in the scope above. Lay one down in Learning Assets first — the Lesson Review Script page.";

export const LESSON_LEVEL_LABEL = "Level";

export const LESSON_LESSON_LABEL = "Lesson";

export const LESSON_SCRIPT_INDEX_LABEL = "Lesson Index";

export const LESSON_CHANGED = "The lesson script changed since this conversation started.";

export const RESTART_WITH_LESSON = "Restart with this script";

export const GRADING_TITLE = "Verdict";

export const GRADING_OUTCOME_LABEL: Record<string, string> = {
  correct: "Correct — next cue",
  retry: "Wrong — hint, same line",
  giving_up: "Read as giving up — answer revealed",
  max_retries: "Out of tries — answer revealed",
};

export const GRADING_EXPECTED = "Expected";

export const GRADING_JACCARD = (score: number | undefined, threshold: number) =>
  `overlap ${score === undefined ? "—" : score.toFixed(2)} of ${threshold.toFixed(2)}`;

// 🔴 Above the jaccard threshold neither judge runs: an edited judge prompt changing nothing here is expected.
export const GRADING_FAST_PATH = "close enough on wording — neither judge ran";

export const GRADING_JUDGE_CORRECT = "Correctness judge";

export const GRADING_JUDGE_ENGAGEMENT = "Giving-up judge";

export const GRADING_NOT_RUN = "did not run";

export const GRADING_YES = "yes";

export const GRADING_NO = "no";

export const GRADING_RETRIES = (used: number, max: number) => `try ${used} of ${max}`;

export const GRADING_COMPARED = "Compared";

export const FACTS_RAN_ON = "Ran on";

export const FLOWS_TITLE = (count: number) => `Flows in this turn (${count})`;

export const FACTS_ROUNDS = (round: number, isArmed: boolean) =>
  `round ${round}${isArmed ? " · winding down from here" : ""}`;

export const FACTS_TOKENS = (input?: number, output?: number) => `${input ?? 0} in / ${output ?? 0} out`;

// 🔴 Priced by the backend: cached input is a subset of input and reasoning is inside output, so rate × tokens here was up to 2× off.
export const FACTS_COST = (usd: number) => `reply ${usd.toFixed(4)} USD`;

export const FACTS_UNPRICED = (models: string[]) => (models.length ? `no rate for ${models.join(", ")}` : "not priced");

export const VIEW_RENDERED_PROMPTS = "View rendered prompts";

export const RENDERED_PROMPTS_TITLE = "Rendered prompts";

export const RENDERED_EMPTY = "The flow rendered no prompts.";

export const RENDERED_PROMPT_OPTION = (displayName: string, source: string, flowVersion?: number) =>
  `${displayName} · ${source}${flowVersion ? ` v${flowVersion}` : ""}`;

export const RENDERED_PROMPT_UNSEEDED = "This prompt has no text here yet.";

export const NOT_SIMULATED_VARIABLES = (names: string[]) => `Left empty by the sandbox: ${names.join(", ")}`;
